// Shared vocabulary for the VMS modules that hang off a visitor record: visits,
// pre-approvals, gate passes, deliveries, cabs, activity logs and settings.
//
// Visitor-master vocabulary (visitor types, ID proofs, phone helpers) lives in
// visitorConstants.js and is imported from there rather than duplicated.
//
// Tailwind's JIT only sees class names it can read literally, so every style
// variant is spelled out instead of being assembled at render time.

import { humanize } from './visitorConstants';
import { formatIstDate, formatIstDateTime } from '../../../utils/helpers/istDate';

export { humanize };

/* ------------------------------------------------------------------ enums */

export const PURPOSES = ['PERSONAL', 'DELIVERY', 'CAB', 'MAINTENANCE', 'OFFICIAL', 'EVENT', 'OTHER'];

// The general vehicle enum, used by visits and gate passes.
export const VEHICLE_TYPES = ['CAR', 'BIKE', 'SCOOTER', 'BICYCLE', 'AUTO', 'TRUCK', 'VAN', 'OTHER'];

// Cab logs accept a narrower set — no SCOOTER, BICYCLE or TRUCK. Kept separate
// on purpose: sharing one dropdown across both forms would produce values the
// cab endpoint rejects.
export const CAB_VEHICLE_TYPES = ['CAR', 'BIKE', 'AUTO', 'VAN', 'OTHER'];

export const VERIFICATION_MODES = ['OTP', 'SIMPLE', 'NONE'];

export const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export const VISIT_STATUSES = [
    'PENDING_APPROVAL', 'PENDING_OTP', 'APPROVED', 'REJECTED',
    'CHECKED_IN', 'CHECKED_OUT', 'CANCELLED', 'EXPIRED',
];

export const PRE_APPROVAL_STATUSES = ['ACTIVE', 'USED', 'EXPIRED', 'CANCELLED'];

export const GATE_PASS_STATUSES = ['ACTIVE', 'EXPIRED', 'REVOKED', 'SUSPENDED'];
export const GATE_PASS_TYPES = ['TEMPORARY', 'RECURRING', 'PERMANENT'];

export const DELIVERY_STATUSES = [
    'PENDING', 'AT_GATE', 'DELIVERED_TO_RESIDENT', 'DELIVERED_TO_GUARD',
    'HELD_AT_GATE', 'REJECTED', 'RETURNED', 'CANCELLED',
];
export const DELIVERY_TYPES = [
    'PARCEL', 'FOOD', 'GROCERY', 'COURIER', 'DOCUMENT',
    'MEDICINE', 'LAUNDRY', 'WATER_CAN', 'GAS_CYLINDER', 'OTHER',
];

export const CAB_STATUSES = ['EXPECTED', 'ARRIVED', 'WAITING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'NO_SHOW'];
export const CAB_TYPES = ['PICKUP', 'DROP', 'RENTAL', 'SHUTTLE', 'OTHER'];

export const GATE_ENTRY_MODES = ['APPROVAL_REQUIRED', 'DIRECT_ENTRY'];

// PRE_APPROVAL_USED and GATE_PASS_USED are declared by the backend but never
// written by any code path, so they are deliberately absent from the filter
// list — offering them would only ever return an empty result.
export const ACTIVITY_TYPES = [
    'VISITOR_REGISTERED', 'VISIT_CREATED', 'APPROVAL_REQUESTED', 'APPROVED', 'REJECTED',
    'OTP_SENT', 'OTP_VERIFIED', 'CHECKED_IN', 'CHECKED_OUT',
    'PRE_APPROVAL_CREATED', 'GATE_PASS_ISSUED', 'GATE_PASS_REVOKED',
    'DELIVERY_LOGGED', 'DELIVERY_COMPLETED', 'CAB_LOGGED', 'CAB_COMPLETED',
    'VISITOR_BLOCKED', 'VISITOR_UNBLOCKED', 'SETTINGS_UPDATED',
];

/* ----------------------------------------------------------------- styles */

export const VISIT_STATUS_STYLE = {
    PENDING_APPROVAL: 'tw-bg-amber-100 tw-text-amber-700',
    PENDING_OTP: 'tw-bg-violet-100 tw-text-violet-700',
    APPROVED: 'tw-bg-sky-100 tw-text-sky-700',
    REJECTED: 'tw-bg-rose-100 tw-text-rose-700',
    CHECKED_IN: 'tw-bg-emerald-100 tw-text-emerald-700',
    CHECKED_OUT: 'tw-bg-slate-100 tw-text-slate-600',
    CANCELLED: 'tw-bg-gray-200 tw-text-gray-600',
    EXPIRED: 'tw-bg-orange-100 tw-text-orange-700',
};

export const VISIT_STATUS_ICON = {
    PENDING_APPROVAL: 'mdi:clock-alert-outline',
    PENDING_OTP: 'mdi:shield-key-outline',
    APPROVED: 'mdi:check-decagram-outline',
    REJECTED: 'mdi:close-octagon-outline',
    CHECKED_IN: 'mdi:login',
    CHECKED_OUT: 'mdi:logout',
    CANCELLED: 'mdi:cancel',
    EXPIRED: 'mdi:timer-off-outline',
};

export const PRE_APPROVAL_STATUS_STYLE = {
    ACTIVE: 'tw-bg-emerald-100 tw-text-emerald-700',
    USED: 'tw-bg-slate-100 tw-text-slate-600',
    EXPIRED: 'tw-bg-orange-100 tw-text-orange-700',
    CANCELLED: 'tw-bg-gray-200 tw-text-gray-600',
};

export const GATE_PASS_STATUS_STYLE = {
    ACTIVE: 'tw-bg-emerald-100 tw-text-emerald-700',
    EXPIRED: 'tw-bg-orange-100 tw-text-orange-700',
    REVOKED: 'tw-bg-rose-100 tw-text-rose-700',
    SUSPENDED: 'tw-bg-amber-100 tw-text-amber-700',
};

export const GATE_PASS_TYPE_STYLE = {
    TEMPORARY: 'tw-bg-sky-100 tw-text-sky-700',
    RECURRING: 'tw-bg-violet-100 tw-text-violet-700',
    PERMANENT: 'tw-bg-teal-100 tw-text-teal-700',
};

export const DELIVERY_STATUS_STYLE = {
    PENDING: 'tw-bg-slate-100 tw-text-slate-600',
    AT_GATE: 'tw-bg-amber-100 tw-text-amber-700',
    DELIVERED_TO_RESIDENT: 'tw-bg-emerald-100 tw-text-emerald-700',
    DELIVERED_TO_GUARD: 'tw-bg-teal-100 tw-text-teal-700',
    HELD_AT_GATE: 'tw-bg-orange-100 tw-text-orange-700',
    REJECTED: 'tw-bg-rose-100 tw-text-rose-700',
    RETURNED: 'tw-bg-violet-100 tw-text-violet-700',
    CANCELLED: 'tw-bg-gray-200 tw-text-gray-600',
};

export const DELIVERY_TYPE_ICON = {
    PARCEL: 'mdi:package-variant-closed',
    FOOD: 'mdi:food-outline',
    GROCERY: 'mdi:basket-outline',
    COURIER: 'mdi:truck-fast-outline',
    DOCUMENT: 'mdi:file-document-outline',
    MEDICINE: 'mdi:pill',
    LAUNDRY: 'mdi:tshirt-crew-outline',
    WATER_CAN: 'mdi:water-outline',
    GAS_CYLINDER: 'mdi:gas-cylinder',
    OTHER: 'mdi:dots-horizontal-circle-outline',
};

export const CAB_STATUS_STYLE = {
    EXPECTED: 'tw-bg-slate-100 tw-text-slate-600',
    ARRIVED: 'tw-bg-amber-100 tw-text-amber-700',
    WAITING: 'tw-bg-yellow-100 tw-text-yellow-800',
    IN_PROGRESS: 'tw-bg-sky-100 tw-text-sky-700',
    COMPLETED: 'tw-bg-emerald-100 tw-text-emerald-700',
    CANCELLED: 'tw-bg-gray-200 tw-text-gray-600',
    NO_SHOW: 'tw-bg-rose-100 tw-text-rose-700',
};

export const CAB_TYPE_ICON = {
    PICKUP: 'mdi:car-arrow-right',
    DROP: 'mdi:car-arrow-left',
    RENTAL: 'mdi:car-key',
    SHUTTLE: 'mdi:bus',
    OTHER: 'mdi:car-outline',
};

export const PURPOSE_ICON = {
    PERSONAL: 'mdi:account-heart-outline',
    DELIVERY: 'mdi:package-variant-closed',
    CAB: 'mdi:taxi',
    MAINTENANCE: 'mdi:wrench-outline',
    OFFICIAL: 'mdi:briefcase-outline',
    EVENT: 'mdi:calendar-star',
    OTHER: 'mdi:dots-horizontal-circle-outline',
};

export const ACTIVITY_ICON = {
    VISITOR_REGISTERED: 'mdi:account-plus-outline',
    VISIT_CREATED: 'mdi:door-open',
    APPROVAL_REQUESTED: 'mdi:clock-alert-outline',
    APPROVED: 'mdi:check-decagram-outline',
    REJECTED: 'mdi:close-octagon-outline',
    OTP_SENT: 'mdi:message-key-outline',
    OTP_VERIFIED: 'mdi:shield-check-outline',
    CHECKED_IN: 'mdi:login',
    CHECKED_OUT: 'mdi:logout',
    PRE_APPROVAL_CREATED: 'mdi:calendar-check-outline',
    GATE_PASS_ISSUED: 'mdi:card-account-details-outline',
    GATE_PASS_REVOKED: 'mdi:card-remove-outline',
    DELIVERY_LOGGED: 'mdi:package-variant-closed',
    DELIVERY_COMPLETED: 'mdi:package-variant-closed-check',
    CAB_LOGGED: 'mdi:taxi',
    CAB_COMPLETED: 'mdi:car-check',
    VISITOR_BLOCKED: 'mdi:block-helper',
    VISITOR_UNBLOCKED: 'mdi:lock-open-variant-outline',
    SETTINGS_UPDATED: 'mdi:cog-outline',
};

export const ACTIVITY_TONE = {
    APPROVED: 'tw-bg-emerald-50 tw-text-emerald-600',
    OTP_VERIFIED: 'tw-bg-emerald-50 tw-text-emerald-600',
    CHECKED_IN: 'tw-bg-emerald-50 tw-text-emerald-600',
    DELIVERY_COMPLETED: 'tw-bg-emerald-50 tw-text-emerald-600',
    CAB_COMPLETED: 'tw-bg-emerald-50 tw-text-emerald-600',
    VISITOR_UNBLOCKED: 'tw-bg-emerald-50 tw-text-emerald-600',
    REJECTED: 'tw-bg-rose-50 tw-text-rose-600',
    VISITOR_BLOCKED: 'tw-bg-rose-50 tw-text-rose-600',
    GATE_PASS_REVOKED: 'tw-bg-rose-50 tw-text-rose-600',
    APPROVAL_REQUESTED: 'tw-bg-amber-50 tw-text-amber-600',
    OTP_SENT: 'tw-bg-violet-50 tw-text-violet-600',
    CHECKED_OUT: 'tw-bg-slate-100 tw-text-slate-600',
    SETTINGS_UPDATED: 'tw-bg-slate-100 tw-text-slate-600',
};

/* ---------------------------------------------------------------- helpers */

// Statuses past which a record can no longer be acted on. The API has no guard
// on delivery/cab "complete", so a terminal record can be completed twice and
// silently overwrite its timestamp — the UI is what has to stop that.
export const VISIT_TERMINAL = ['CHECKED_OUT', 'REJECTED', 'CANCELLED', 'EXPIRED'];
export const DELIVERY_TERMINAL = ['DELIVERED_TO_RESIDENT', 'DELIVERED_TO_GUARD', 'REJECTED', 'RETURNED', 'CANCELLED'];
export const CAB_TERMINAL = ['COMPLETED', 'CANCELLED', 'NO_SHOW'];

export const isVisitTerminal = (v) => VISIT_TERMINAL.includes(v?.status);
export const isDeliveryTerminal = (d) => DELIVERY_TERMINAL.includes(d?.status);
export const isCabTerminal = (c) => CAB_TERMINAL.includes(c?.status);

// Nothing in the backend ever sets EXPIRED on a pre-approval or gate pass — an
// out-of-window record keeps reading ACTIVE and is simply skipped by the
// matcher. Recompute it here so the UI does not promise an authorisation that
// will not actually open the gate.
const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

export const isWindowExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < startOfToday();
};

export const isWindowPending = (validFrom) => {
    if (!validFrom) return false;
    return new Date(validFrom) > new Date();
};

// Effective status for display: promotes a stale ACTIVE row to EXPIRED.
export const effectivePreApprovalStatus = (p) =>
    (p?.status === 'ACTIVE' && isWindowExpired(p.valid_until)) ? 'EXPIRED' : (p?.status || 'ACTIVE');

export const effectiveGatePassStatus = (g) =>
    (g?.status === 'ACTIVE' && isWindowExpired(g.valid_until)) ? 'EXPIRED' : (g?.status || 'ACTIVE');

export const fmtDate = (d) =>
    d ? formatIstDate(d) : '—';

export const fmtDateTime = (d) =>
    d ? formatIstDateTime(d) : '—';

export const fmtTime = (t) => {
    if (!t) return '—';
    // Times come back as HH:MM:SS; only the wall-clock part is meaningful.
    const [h, m] = String(t).split(':');
    if (h === undefined || m === undefined) return String(t);
    const hour = Number(h);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const display = hour % 12 === 0 ? 12 : hour % 12;
    return `${display}:${m} ${suffix}`;
};

// <input type="datetime-local"> needs `YYYY-MM-DDTHH:mm` in local time; a raw
// ISO string with a Z would be silently rejected by the control.
export const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Gate pass validity columns are DATEONLY — any time component is discarded by
// the server, so send a plain date.
export const toDateInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const flatLabel = (row) => {
    const wing = row?.wing?.wing_name || row?.wing?.title;
    const flat = row?.flat?.flat_no || row?.flat?.flat_number;
    if (!wing && !flat) return '—';
    return [wing, flat].filter(Boolean).join(' · ');
};

export const personName = (u) => {
    if (!u) return '—';
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return full || u.email || `User #${u.user_id}`;
};

// Options shaped for SearchableDropdown, which the whole module uses instead of
// native selects.
export const toOptions = (values, label = humanize) =>
    values.map(v => ({ value: v, label: label(v) }));
